import { Component, OnInit, Input, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { camelCase } from 'lodash';
// Services
import { LoggerService, BroadcastService, ObjectUtils } from '@cv-invision/core';
import { ViewsService } from '../../../../../services/views.service';
import { SchemasService } from '../../../../../services/schemas.service';
import { VisibilityContext } from '../../../../../services/visibility-context';
import { LayoutRenderService } from '../../../services/layout-render.service';
import { FormFieldShowOptions } from '../../models/component-options/form-field.options';

@Component({
	selector: 'cv-schema-render-form-field',
	templateUrl: './form-field.component.html'
})
export class FormFieldComponent implements OnInit, OnDestroy {
	@ViewChild('reviewField', { read: ElementRef, static: false }) reviewField: ElementRef;
	@Input() options: any;
	@Input() indexTab: any;

	formControl: FormControl;
	field: any;
	value: any;
	historical = [];

	paths = {
		original: '',
		jsonPath: '',
		schema: '',
		path: ''
	};

	show = new FormFieldShowOptions(true, true);

	visible = true;
	active = false;

	get form(): FormGroup {
		return this.viewsService.form;
	}

	get historicalValues(): any {
		return this.viewsService.historical;
	}

	get isEditing(): boolean {
		let response = this.layoutService.activateEditableUX;
		const containsOption = 'editable' in this.options;

		if (containsOption) {
			response = this.layoutService.activateEditableUX && this.options?.editable === true;
		}
		return response;
	}

	private atComponentDestroy = new Subject();

	constructor(
		private viewsService: ViewsService,
		protected schemaService: SchemasService,
		private broadcastService: BroadcastService,
		private layoutService: LayoutRenderService,
		private console: LoggerService
	) {
		//
	}

	async ngOnInit(): Promise<void> {
		if (this.options?.displayType === 'PublicRecord') {
			return;
		}
		await this.schemaService.setPaths(this.paths, this.options);
		await this.setFormControl().then(()=>{
			if (this.options?.validators && this.options.validators.length > 0) {
				const formValidators = [];

				this.options.validators.forEach(validator=>{
					const validatorParts = validator.split(':');
					switch(validatorParts[0]) {
						case 'required':
							formValidators.push(Validators.required);
							break;
						case 'digits':
							const pattern = new RegExp(`^\\d{${validatorParts[1]}}$`);
							formValidators.push(Validators.pattern(pattern));
							break;
					}
				});

				this.formControl.setValidators(formValidators);
				this.formControl.updateValueAndValidity();
			}
		});

		await this.setField();
		await this.setValue();

		// await this.setHistorical();
		await this.isVisible();

		// this.viewsService.historicalChanged.pipe(takeUntil(this.atComponentDestroy)).subscribe(async data => {
		// 	await this.setHistorical();
		// });

		this.viewsService.selectedRuleChanged.pipe(takeUntil(this.atComponentDestroy)).subscribe(async rule => {
			await this.highlightField(rule);
		});

		this.form.valueChanges.pipe(takeUntil(this.atComponentDestroy)).subscribe(async data => {
			await this.setValue();
			await this.isVisible();
		});
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * Set the field different paths,
	 */
	async setField(): Promise<void> {
		const field = await this.schemaService.getMetadataByPath(this.paths.schema);
		this.field = ObjectUtils.recursiveDeepCopy(field);
		if ('show' in this.options) {
			Object.assign(this.show, this.options.show);
		}

		if ('label' in this.options) {
			const newLabels = {
				UILabel: this.options.label,
				DisplayText: this.options.label
			};

			Object.assign(this.field, newLabels);
		}
	}

	/**
	 * Set the value of the field
	 */
	async setValue(): Promise<void> {
		this.value = '--';

		this.value = this.formControl.value;
		if (this.field && this.field.DataType === 'Enumerated') {
			const enumValue = this.schemaService.getEnumeratedValue(this.paths.schema, this.formControl.value);
			this.value = enumValue;
		}
	}

	/**
	 * initialize formcontrol from the given path
	 */
	async setFormControl(): Promise<any> {
		let formData: any = this.form;

		this.paths.path.split('.').forEach(path => {
			const pathPattern = /(\[[0-9]+\])/g;
			const matches = path.match(pathPattern);
			let isArray = false;
			let pathIndex;

			if (matches) {
				isArray = true;
				pathIndex = matches[0].replace(/([[\]])/g, '');
				path = path.replace(pathPattern, '');
			}

			if (formData.get(path) === null) {
				this.createNewControl(formData, path);
			}

			formData = formData.get(path);

			if (isArray) {
				formData = formData.at(pathIndex);
			}
		});
		this.formControl = formData;
	}

	/**
	 * Create the formcontrol for the given path if it does not exist, just temporary
	 *
	 * @param fb FormGroup
	 * @param fieldName the path to the formcontrol
	 */
	createNewControl(fb: FormGroup, fieldName: string): void {
		fb.addControl(fieldName, new FormControl('', []));
	}

	/**
	 * Set the historical array values
	 */
	async setHistorical(): Promise<void> {
		const pattern = `$..[?(@.path=="${this.paths.original}")]`;
		this.historical = (this.schemaService.query(this.historicalValues, pattern) || []).map(jpChanges => {
			return {
				Val: jpChanges.previousValue
			};
		});
	}

	/**
	 * Set the historical array values for non standarizes return values
	 */
	async setHistoricalNonStandarized(fieldValue: any): Promise<void> {
		if (fieldValue.Historical.length > 0) {
			const formated = [];
			fieldValue.Historical.forEach(item => {
				const groupParse: any = [];
				Object.keys(item).forEach(key => {
					const nKey = camelCase(key);
					groupParse[nKey] = item[key];
				});
				formated.push(Object.assign({}, groupParse));
			});
			this.historical = formated;
		}
	}

	/**
	 * open historical component
	 * @param item
	 * @param historical
	 */
	async showHistorical(elem: ElementRef): Promise<void> {
		const { height, left, width, top } = await elem.nativeElement.getBoundingClientRect();
		const offsetHeight = height;
		const offsetLeft = left - (width / 2);
		const offsetTop = top;
		const offsetWidth = width;
		const scrollTop = 0;
		this.broadcastService.broadcast({
			name: 'historicalValueEvent',
			event: {
				isOpen: true,
				data: this.historical,
				pos: { offsetHeight, offsetLeft, offsetTop, offsetWidth, scrollTop }
			}
		});
	}

	/**
	 * Evaluate the conditionality to show/hide the field
	 */
	async isVisible(): Promise<void> {
		let visible = true;
		if (this.field?.Conditionally !== undefined && typeof this.field?.Conditionally === 'function') {
			// tslint:disable-next-line:no-eval
			// const evaluation: any = await eval(`(${this.field.Conditionally})`)({}, new VisibilityContext(this.formControl, this.schemaService));
			const evaluation = await this.field.Conditionally({}, new VisibilityContext(this.formControl, this.schemaService));
			visible = evaluation as boolean;

			if (!visible) {
				this.formControl.setValue('', { onlySelf: true, emitEvent: false });
			}
		}

		if ('hidden' in this.options && this.options.hidden) {
			visible = false;
		}

		if ('hideFields' in this.options && this.options.hideFields.length) {
			visible = !((this.options.hideFields || []).indexOf(this.paths.schema) > -1);
		}

		this.visible = visible;
	}

	/**
	 * Mark the field as active and scroll to the element
	 * @param rule
	 */
	async highlightField(rule: any): Promise<void> {
		this.active = rule.indexOf(this.paths.original) > -1;
		if (this.active) {
			let scrollOptions: any = false;
			if (window.innerHeight > 750) {
				scrollOptions = {
					behavior: 'smooth',
					block: 'start'
				};
			}
			this.reviewField.nativeElement.scrollIntoView(scrollOptions);
		}
	}
}
